import { useCallback, useEffect, useState } from 'react';
import type { Stripe } from '@stripe/stripe-js';
import {
  postCreateSubOrder,
  postCreateExtraMemberOrder,
  type SubOrderUnit,
} from '@/api/modules/team';
import { postCreateStripeIntent } from '@/api/modules/subpayment';
import { userFacingError } from '@/lib/userFacingError';
import { StripeCustomCardForm } from '@/pages/subscribe/components/StripeCustomCardForm';

type BillingPeriod = 'monthly' | 'yearly';

export interface SubscribePaymentSession {
  clientSecret: string;
  orderId: number | string;
  paymentIntentId: string;
}

function billingToUnit(period: BillingPeriod): SubOrderUnit {
  return period === 'monthly' ? 0 : 1;
}

export type SubscribeOrderKind = 'subscription' | 'extra_members';

export interface SubscribePaymentFormProps {
  billing: BillingPeriod;
  amountLabel: string;
  stripePromise: Promise<Stripe | null> | null;
  disabled?: boolean;
  /** 续订/改套餐时传入增购席位总数；不传则沿用团队当前增购人数 */
  extraMembers?: number;
  orderKind?: SubscribeOrderKind;
}

export function SubscribePaymentForm({
  billing,
  amountLabel,
  stripePromise,
  disabled = false,
  extraMembers,
  orderKind = 'subscription',
}: SubscribePaymentFormProps) {
  const [stripeSession, setStripeSession] = useState<SubscribePaymentSession | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);

  const stripeAvailable = Boolean(stripePromise);

  const resetCheckoutState = useCallback(() => {
    setStripeSession(null);
    setPayError(null);
  }, []);

  useEffect(() => {
    resetCheckoutState();
  }, [billing, extraMembers, orderKind, resetCheckoutState]);

  const prepareStripe = async () => {
    const order =
      orderKind === 'extra_members'
        ? await postCreateExtraMemberOrder(Math.max(1, extraMembers ?? 1))
        : await postCreateSubOrder({
            number: 1,
            unit: billingToUnit(billing),
            extraMembers,
          });
    const intent = await postCreateStripeIntent(order.orderId);
    setStripeSession({
      clientSecret: intent.clientSecret,
      orderId: order.orderId,
      paymentIntentId: intent.paymentIntentId,
    });
  };

  const handleContinue = async () => {
    if (!stripeAvailable) {
      setPayError('Stripe is not available.');
      return;
    }

    setPreparing(true);
    setPayError(null);
    try {
      await prepareStripe();
    } catch (e) {
      setPayError(userFacingError(e, 'Failed to prepare payment. Please try again.'));
    } finally {
      setPreparing(false);
    }
  };

  const showContinue = !stripeSession;

  if (!stripeAvailable) {
    return (
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
        Card payment is temporarily unavailable. Please try again later.
      </p>
    );
  }

  return (
    <>
      {stripeSession && stripePromise && (
        <StripeCustomCardForm
          key={stripeSession.clientSecret}
          stripePromise={stripePromise}
          session={stripeSession}
          amountLabel={amountLabel}
          billing={billing}
          onError={(msg) => setPayError(msg || null)}
        />
      )}

      {showContinue && (
        <p className="text-gray-500 text-xs mb-4 text-center">
          Continue to create your order and enter card details.
        </p>
      )}

      {payError && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-4">
          {payError}
        </div>
      )}

      {showContinue && (
        <button
          type="button"
          onClick={() => void handleContinue()}
          disabled={disabled || preparing}
          className="w-full h-11 rounded-lg bg-green-500 text-white font-normal text-sm cursor-pointer whitespace-nowrap hover:bg-green-600 transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
        >
          {preparing ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base" />
              Preparing...
            </>
          ) : (
            <>
              <i className="ri-shield-check-line text-base" />
              Continue - {amountLabel}
            </>
          )}
        </button>
      )}
    </>
  );
}
